import create, { UseStore } from 'zustand';
import { fetchRequest, log } from './';
import { AlertStore } from './alerts';
import { config } from './config';
import { DateInput } from './date-time';
import { Worksheet, WorksheetComponents, WorksheetStateAction, WorksheetStateInfo, serviceDate } from './domain';
import { FormApi, FormProps, formApi } from './form';
import { StatusApi } from './status';
import { currentWorkflow } from './workflow-api';
import { WorksheetAction, WorksheetResult, worksheetMessage } from './worksheet-service';
import { checkAlertStatus } from './alerts/alerts-api';

type Action = {
  worksheetId: string;
  action: WorksheetAction;
};

export type ActionArgs = Action[];
const endpoint = () => config.codingUrl + '/worksheet/process';
export interface VisitTracking {
  visitId?: string;
  code?: string;
  description?: string;
  injuryDate?: DateInput;
  injuryState?: string;
}
export interface TransitionPayload {
  visitId: string;
  worksheetComponents: WorksheetComponents[];
  arrivalMode?: VisitTracking;
  disposition?: VisitTracking;
  injury?: VisitTracking;
}
export type TransitionResult = WorksheetResult<TransitionPayload>;

type TransitionArgs = string[];

const actions = (worksheetIds: string[], action: WorksheetAction): Action[] => worksheetIds.map((worksheetId) => ({ worksheetId, action }));

export const transitionRequest = (action: string) =>
  fetchRequest<TransitionArgs, TransitionResult>({
    method: 'post',
    body: (args: TransitionArgs) => worksheetMessage(actions(args, action as any), 'transition', 'worksheet', currentWorkflow()),
  });

export const checkoutTransition = transitionRequest('checkout');
export const unassignTransition = transitionRequest('unassign');

export type WorksheetStateRef = WorksheetStateInfo & {
  worksheetId?: string;
};
type TransitionProps = {
  worksheetState?: WorksheetStateRef;
  worksheet?: Worksheet;
  alerts?: AlertStore;
  status?: StatusApi;
  onError?: () => void;
  transitionGuard?: (action?: WorksheetStateAction) => Promise<boolean>;
  transitionDialogueComplete?: (action?: WorksheetStateAction) => void;
};
type TransitionApi = TransitionProps & {
  openTransitionDialogue: boolean;
  transitionDialogueComplete?: (action?: WorksheetStateAction) => void;
  actions: WorksheetStateAction[];
  alerts: AlertStore;
  setWorksheet(ref: WorksheetStateRef): void;
  transition(action: WorksheetStateAction, transitionComplete?: (action?: WorksheetStateAction) => void): Promise<TransitionResult>;
};

export type TransitionStore = UseStore<TransitionApi>;

export function transitionApi(props?: TransitionProps): TransitionStore {
  const { worksheetState: worksheet, transitionGuard, transitionDialogueComplete, alerts, status } = { ...props };

  function validateWorksheet(w: WorksheetStateRef, validateActions = true) {
    const id = w?.worksheetId;
    const state = w?.stateInfo;
    const actions = state?.actions?.length;

    const errors = id ? '' : ' Missing worksheet id.' + state ? '' : ' Missing transition state.' + validateActions ? (actions ? '' : ' Missing Actions.') : '';

    return errors;
  }

  function errorMessage(message: string) {
    if (status) {
      status.getState().setStatus(message);
    }
    return Promise.reject(message);
  }

  const api = create<TransitionApi>((set, get) => ({
    alerts,
    worksheetState: null,
    actions: [],
    openTransitionDialogue: false,
    transitionDialogueComplete,
    setWorksheet: (w: WorksheetStateRef) => {
      const errors = validateWorksheet(w);

      if (errors.length) {
        throw new Error('Transition API Error: ' + errors);
      }
      set({ worksheetState: w, actions: w.stateInfo.actions });
    },
    transition: async (action: WorksheetStateAction, completeFn?) => {
      if (transitionGuard) {
        const proceed = await transitionGuard(action);
        if (!proceed) {
          return Promise.reject('There are alerts preventing this transition. Please review.');
        }
      }
      const w = get().worksheetState;

      if (!w || !w.worksheetId || !w.stateInfo || !action) {
        return errorMessage('Valid worksheet and action are required for transition API.');
      }

      const result: TransitionResult = await transitionRequest(action.action)([w.worksheetId]);
      const component: WorksheetComponents = result?.result?.worksheetComponents[0];

      const alertStore = get().alerts;
      const alerts = component?.alerts;
      const ref: WorksheetStateRef = {
        ...w,
        ...{ stateInfo: component?.stateInfo },
      };

      // Are alerts being returned by the checkout transition, moving to the next worksheet ?
      // sheet A -> alerts, load sheet B -> see the alerts of A

      if (!result.success) {
        const onError = get().onError;
        if (onError) {
          onError();
        }

        const requestErrors = result.errors && result.errors.length ? result.errors.join('\n') : '';
        
        if (alertStore && alerts?.length) {
          alertStore.setState({ alerts });
          checkAlertStatus();
          return errorMessage('Worksheet transition returned alerts. ' + requestErrors);
        } else {
          if (requestErrors?.length) {
            return errorMessage(requestErrors);
          } else {
            const errors = validateWorksheet(ref, false);
            if (errors.length) {
              return errorMessage('Transition API Error: ' + errors);
            } else if (!ref?.stateInfo) {
              return errorMessage('Invalid Transition Result: No state returned.');
            }
          }
        }
      }

      if (alertStore && action.action !== 'CHECKOUT') {
        alertStore.getState().close();
      }
      //TODO test nested ref update order in zustrand

      get().worksheetState.stateInfo = component.stateInfo;
      set({
        worksheetState: { ...ref },
        actions: ref.stateInfo.actions,
        openTransitionDialogue: false,
      });

      if (completeFn) {
        completeFn(action);
      }

      if (get().transitionDialogueComplete) {
        get().transitionDialogueComplete(action);
      }

      return Promise.resolve(result);
    },
  }));

  if (worksheet) {
    if (worksheet.stateInfo) {
      api.getState().setWorksheet(worksheet);
    } else {
      log.error('Worksheet state info is missing. Transition API will not be available.', worksheet);
    }
  }
  return api;
}

type TransitionFormData = {
  action: WorksheetStateAction;
};
export type TransitionForm = FormApi<TransitionFormData> & {
  refreshActions(): void;
};

export function transitionListForm(api: TransitionStore, formProps?: Partial<FormProps>): TransitionForm {
  const form: Partial<TransitionForm> = formApi<TransitionFormData>({
    ...{
      values: {
        action: null,
      },
      fields: {
        action: {
          required: true,
          label: 'Select a Valid Transition',
          list: api.getState().actions,
          listLabel: (v: WorksheetStateAction) => v.action,
          onEnter: () => form.submit(),
        },
      },
      valid: (v) => !!v.action,
      onSubmit: async (v) => {
        try {
          await api.getState().transition(v.action);
          refreshActions();
        } catch (error) {
          // console.log(error);
          action.value.setState({ error });
        }
      },
      resetValues: () => ({ action: null }),
    },
    ...formProps,
  });
  const { action } = form.controls;

  function refreshActions() {
    // console.log('refreshActions', api.getState().actions)
    action.list.getState().setValue(api.getState().actions);
  }
  return { ...form, refreshActions } as TransitionForm;
}
type GetWorksheetArgs = {
  worksheetId: string | string[];
  includeTransitionLog?: boolean;
};

const getWorksheetRequest = fetchRequest<GetWorksheetArgs, TransitionResult>({
  method: 'post',
  body: (a: GetWorksheetArgs) => ({
    service: 'worksheet',
    action: 'getworksheet',
    data: {
      ...{ includeTransitionLog: a.includeTransitionLog || false },
      ...a,
    },
  }),
});

// pastWorksheets serviceDate is a calendar date; drop the time portion
// (e.g. "2026-03-07T00:00:00.000Z" -> "2026-03-07")
export async function getWorksheet(args: GetWorksheetArgs): Promise<TransitionResult> {
  const r = await getWorksheetRequest(args);
  r?.result?.worksheetComponents?.forEach((c) => {
    c?.worksheet?.pastWorksheets?.forEach((p) => {
      if (p.serviceDate) {
        p.serviceDate = serviceDate(p);
      }
    });
  });
  return r;
}

export type DFT = {
  _id: {
    timestamp: number;
    date: string;
  };
  worksheetId: string;
  componentId: string;
  locationCode: string;
  departmentCode: string;
  worksheetType: string;
  batchId: string;
  proceduresBilled: number;
  procedureIds: string[];
  billingString: string;
  fileName: string;
  s3Bucket: null;
  createDate: string;
};

export const getBillingDft = fetchRequest<string, DFT[]>({
  method: 'get',
  url: (id: string) => `/billing/dft/worksheetid/${id}`,
  notFoundResult: () => [],
});
